import React, { useState, FormEvent } from 'react';
import { Divider } from '@mui/material';
import useCreateBooking, { RoomDetail } from '../../../hooks/useCreateBooking';
import useCreatePaymentIntent from '../../../hooks/useCreatePaymentIntent';
import { PaymentIntentRoomInput } from '../../../gql/graphql';
import { useNavigate } from 'react-router-dom';
import { trackPurchase } from '../../../utils/analytics';
import { clearPaymentDraft } from '../../../utils/paymentDraft';
import {
  firstCheckoutErrorMessage,
  validateCheckoutGuest,
} from '../../../utils/checkoutValidation';
import { buildThankYouPath } from '../../../utils/thankYouNavigation';

interface Props {
  tripId: number;
  boatId: number;
  selectedRooms: RoomDetail[];
  email: string;
  fullName: string;
  gender: string;
  phoneNumber: string;
  paymentOption: number;
  numberOfGuests: number;
  currency: string;
  isAuthenticated: boolean;
  userId: string | undefined;
  couponCode: string;
}

const PayLaterForm: React.FC<Props> = ({
  tripId,
  boatId,
  selectedRooms,
  email,
  fullName,
  gender,
  phoneNumber,
  paymentOption,
  numberOfGuests,
  currency,
  isAuthenticated,
  userId,
  couponCode,
}) => {
  const createBookingMutation = useCreateBooking();
  const createPaymentIntent = useCreatePaymentIntent();
  const navigate = useNavigate();

  const transformRoomsToInput = (
    rooms: RoomDetail[],
  ): PaymentIntentRoomInput[] =>
    rooms.map(room => ({ id: room.typeId, quantity: room.numberOfRooms }));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const errors = validateCheckoutGuest({
      fullName,
      email,
      phone: phoneNumber,
      gender,
    });
    const message = firstCheckoutErrorMessage(errors);
    if (message) {
      setErrorMessage(message);
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    handleBooking();
  };

  const handleBooking = () => {
    // Fire-and-forget: creates a full-price PaymentIntent in Stripe for tracking
    createPaymentIntent.mutate({
      tripId,
      roomIds: transformRoomsToInput(selectedRooms),
      isAuthenticated,
      userId,
      couponCode: couponCode || null,
      boatId: boatId || null,
    });

    createBookingMutation.mutate(
      {
        tripId,
        bookingState: 1,
        rooms: selectedRooms,
        email,
        fullName,
        gender,
        phone: phoneNumber,
        paymentOption,
        numberOfGuests,
      },
      {
        onSuccess: data => {
          setIsLoading(false);
          clearPaymentDraft();

          const totalValue = selectedRooms.reduce(
            (sum, room) => sum + room.pricePerRoom * room.numberOfRooms,
            0,
          );

          const bookingId = data.createBooking.bookingId;
          const thankYouState = {
            bookingId,
            value: totalValue,
            currency,
            paymentPath: 'pay_later' as const,
            tripId,
            boatId,
            paymentSucceeded: true,
          };

          const doNavigate = () => {
            navigate(buildThankYouPath(thankYouState), {
              state: thankYouState,
            });
          };

          trackPurchase(
            {
              bookingId,
              value: totalValue,
              currency,
              paymentPath: 'pay_later',
              tripId,
              boatId,
            },
            doNavigate,
          );
          setTimeout(doNavigate, 1000);
        },
        onError: error => {
          console.error(error);
          setErrorMessage('Failed to create booking. Please try again.');
          setIsLoading(false);
        },
      },
    );
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      {errorMessage && (
        <div className="text-red-500" role="alert">
          {errorMessage}
        </div>
      )}
      <Divider />
      <div className="flex flex-col items-start gap-2 self-stretch">
        <span className="text-neutral-800 font-semibold">
          Cancellation policy
        </span>
        <span className="text-neutral-500 text-sm">
          This booking is non-refundable. Once confirmed, payments are
          non-refundable under any circumstances. Please review your plans
          carefully before booking. For any inquiries, contact our support team.
        </span>
      </div>
      <Divider />
      <button
        className="flex py-4 px-6 items-start justify-center self-stretch rounded-lg bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker text-neutral-00 w-full"
        type="submit"
        disabled={isLoading}
      >
        Reserve your spot for free
      </button>
      <span className="text-neutral-500 text-sm">
        After booking completion, we will collect specific dive-related
        information, including flight details, guest information, and rental
        diving gear requirements.
      </span>
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="bg-white rounded-lg shadow-lg p-6 z-50 w-80">
            <h2 className="text-lg font-semibold">Please wait...</h2>
            <p className="mt-2">
              We&apos;re currently processing your liveaboard trip.
            </p>
          </div>
        </div>
      )}
    </form>
  );
};

export default PayLaterForm;
